import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useCampaignActivity } from '../hooks/useCampaignActivity';
import { ActivityRow } from '../live/ActivityRow';
import type { OutreachActivityEvent } from '../outreachTypes';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import card from '../live/Campaign.module.scss';
import liveStyles from '../live/Live.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DayGroup {
  key: string;
  label: string;
  events: OutreachActivityEvent[];
}

/**
 * "What happened" (design outreach-v2 t6): every send, listen, reply and add of the campaign,
 * newest first, by day (`campaigns.activity`). A reply line has "Answer".
 */
const ActivityFeedComponent: React.FC<{ accountId: number; campaignUuid: string }> = ({ accountId, campaignUuid }) => {
  const intl = useIntl();
  const feed = useCampaignActivity(accountId, campaignUuid);
  const more = useCallback(() => {
    feed.loadMore().catch(console.error);
  }, [feed]);
  const groups = useMemo(() => {
    const list: DayGroup[] = [];
    feed.events.forEach((event) => {
      const at = new Date(event.at);
      const key = intl.formatDate(at, { year: 'numeric', month: '2-digit', day: '2-digit' });
      const last = list[list.length - 1];
      if (last !== undefined && last.key === key) {
        last.events.push(event);
      } else {
        list.push({
          key,
          label: intl.formatDate(at, { weekday: 'short', month: 'short', day: 'numeric' }),
          events: [event],
        });
      }
    });
    return list;
  }, [feed.events, intl]);

  const body = () => {
    if (feed.isLoading) {
      return <LoadingView />;
    }
    if (feed.error !== null) {
      return <ErrorView error={feed.error} retry={feed.retry} />;
    }
    if (groups.length === 0) {
      return <p className={`${shared.small} mb-0`}>{intl.formatMessage(messages.feedEmpty)}</p>;
    }
    return (
      <>
        {groups.map((group) => (
          <div key={group.key}>
            <div className={styles.feedDay}>{group.label}</div>
            <ul className={`${liveStyles.timeline} p-0 m-0`}>
              {group.events.map((event) => (
                <ActivityRow key={event.id} event={event} />
              ))}
            </ul>
          </div>
        ))}
        {feed.hasMore && (
          <div className="d-flex justify-content-center pt-3">
            <SMLButton size="sm" outline omitCap onClick={more} isLoading={feed.isLoadingMore} testID="feed-more">
              {intl.formatMessage(messages.feedMore)}
            </SMLButton>
          </div>
        )}
      </>
    );
  };

  return (
    <section className={card.card} data-testid="results-feed">
      <h3 className={card.cardTitle}>{intl.formatMessage(messages.feedTitle)}</h3>
      {body()}
    </section>
  );
};

export const ActivityFeed = TypedMemo(ActivityFeedComponent);
